import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { useSite } from "../lib/site";
import { CONTACT, SERVICE_KEYS } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", message: "" };

const Details = () => {
  const { t, lang } = useSite();
  return (
    <div className="space-y-10">
      <div>
        <p className="eyebrow mb-4">{t.contact.call}</p>
        {CONTACT.phones.map((p, i) => (
          <a key={p} href={`tel:${p}`} dir="ltr" data-testid={`contact-phone-${i + 1}`} className="group flex items-center gap-3 f-display text-2xl sm:text-3xl text-[var(--paper)] hover:text-[var(--gold)] transition-colors py-1 w-fit">
            <Phone className="w-5 h-5 text-[var(--gold)]" />{p}
          </a>
        ))}
      </div>
      <div>
        <p className="eyebrow mb-4">{t.contact.visit}</p>
        <p data-testid="contact-address" className="flex gap-3 text-lg text-[var(--sand)] max-w-xs leading-relaxed">
          <MapPin className="w-5 h-5 mt-1 shrink-0 text-[var(--gold)]" />
          {lang === "ar" ? CONTACT.addressAr : CONTACT.address}
        </p>
      </div>
    </div>
  );
};

export const Contact = () => {
  const { t, lang, service, setService } = useSite();
  const f = t.contact.form;
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, service: service || "general", lang });
      toast.success(t.contact.ok);
      setForm(EMPTY);
      setService("");
    } catch {
      toast.error(t.contact.err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-36 bg-[var(--ink-2)] border-t border-[var(--line)]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead id="contact" eyebrow={t.contact.eyebrow} title={t.contact.title} />
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5"><Details /></Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6" data-testid="contact-form">
              <input required minLength={2} className="field" placeholder={f.name} value={form.name} onChange={set("name")} data-testid="contact-form-name" />
              <input required type="email" className="field" placeholder={f.email} value={form.email} onChange={set("email")} data-testid="contact-form-email" />
              <input className="field" placeholder={f.phone} value={form.phone} onChange={set("phone")} data-testid="contact-form-phone" />
              <select className="field" value={service} onChange={(e) => setService(e.target.value)} data-testid="contact-form-service" aria-label={f.service}>
                <option value="">{f.any}</option>
                {SERVICE_KEYS.map((k) => <option key={k} value={k}>{t.services.items[k].title}</option>)}
              </select>
              <textarea required minLength={5} rows={4} className="field sm:col-span-2 resize-none" placeholder={f.message} value={form.message} onChange={set("message")} data-testid="contact-form-message" />
              <div className="sm:col-span-2 pt-4">
                <button type="submit" disabled={sending} data-testid="contact-form-submit-button" className="group inline-flex items-center gap-3 rounded-full bg-[var(--gold)] px-8 py-4 font-medium text-[var(--ink)] hover:bg-[var(--sand)] disabled:opacity-60 transition-colors duration-300">
                  {sending ? f.sending : f.submit}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
